import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  Clock,
  Wrench,
  PocketKnife,
  Image as ImageIcon,
  Paperclip,
  BookOpen,
} from 'lucide-react';
import MoldStatus from '../shared/MoldStatus';
import AuditLogsPanel from '../shared/AuditLogsPanel';
import { TabButtonProps } from './types';
import { Mold, MoldDetailTab, ProductPart } from '../../types/mold';
import ActionButtons from './ActionButtons';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import ShotCountChangeConfirmDialog from './ShotCountChangeConfirmDialog';
import { useMoldOrders } from '../../hooks/useMoldOrders';
import MoldInformation from './MoldInformation';
import SearchPartModal from './SearchPartModal';
import MoldDetailSkeleton from './MoldDetailSkeleton';
import SearchInsertModal from './SearchInsertModal'; // Add this import
import { usePreferences } from '../../context/PreferencesContext';
import ProductPartManager from '../shared/ProductPartManager';
import InsertManager from '../shared/InsertManager';
import MoldOrderList from '../MoldOrderList';
import MoldImages, { MoldImageTile } from './MoldImages';
import MoldAttachments from './MoldAttachments';
import MoldGuides from './MoldGuides';
import { useAppState } from '../../context/AppStateContext';

const MoldOrders = ({ moldId }: {
  moldId: string;
}) => {
  const { moldOrders, loading, error, fetchMoldOrders } = useMoldOrders();
  const { preferences } = usePreferences();

  useEffect(() => {
    fetchMoldOrders(parseInt(moldId));
  }, [moldId, fetchMoldOrders]);

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="text-red-500 py-4">
        Error loading mold orders: {error}
      </div>
    );
  }

  if (!moldOrders || moldOrders.length === 0) {
    return (
      <>
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <FileText className="mr-2" size={20} />
          ใบงานที่เกี่ยวข้อง
        </h2>
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200 text-center gap-4">
          <div className="bg-blue-50 p-6 rounded-full shadow-sm">
            <FileText size={40} className="text-blue-500" />
          </div>
          <p className="text-lg font-medium text-gray-700 mb-2">ยังไม่มีใบงานสำหรับแม่พิมพ์นี้</p>
        </div>
      </>
    );
  }

  return (
    <MoldOrderList
      moldOrders={moldOrders}
      preferences={preferences}
      searchTerm={''}
      selectedSearchField={'all'}
      bgColorCode={'white'}
      preferenceContext={'moldDetail'}
      CustomHeader={() => {
        return <h2 className="text-xl font-bold mb-4 flex items-center">
          <FileText className="mr-2" size={20} />
          ใบงานที่เกี่ยวข้อง
        </h2>
      }}
      hidePadding={true}
    />
  );
};

interface MoldDetailProps {
  mold: Mold;
  editedMold: Mold;
  isEditing: boolean;
  loading: boolean;
  error: string | null;
  fieldErrors?: Record<string, boolean>;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onFieldChange: (field: keyof Mold, value: any) => void;
  associatePart: (moldId: string, partId: number) => Promise<void>;
  deleteAssociatedPart: (moldId: string, partId: number) => Promise<void>;
  associateInsert: (moldId: string, insertId: number) => Promise<void>;
  deleteAssociatedInsert: (moldId: string, insertId: number) => Promise<void>;
  successMessage: string | null;
}

const MoldDetail: React.FC<MoldDetailProps> = ({
  mold,
  editedMold,
  isEditing,
  loading,
  error,
  fieldErrors = {},
  onEdit,
  onCancel,
  onSave,
  onFieldChange,
  associatePart,
  deleteAssociatedPart,
  associateInsert,
  deleteAssociatedInsert,
  successMessage,
}) => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return (
      <div className="flex items-center justify-center h-screen">
        Invalid mold ID
      </div>
    );
  }
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(
    null,
  );
  const { preferences, updatePreference } = usePreferences();
  const { featureToggles } = useAppState();
  const isMoldDetailTabEnabled = (tab: MoldDetailTab) =>
    (tab !== 'guides' || featureToggles.enableMoldGuides);
  const preferredTab = preferences.moldDetailTab || 'tasks';
  const [activeTab, setActiveTab] = useState<MoldDetailTab>(
    isMoldDetailTabEnabled(preferredTab) ? preferredTab : 'tasks',
  );
  const [showSearchPartModal, setShowSearchPartModal] = useState(false); // Add this state
  const [showSearchInsertModal, setShowSearchInsertModal] = useState(false); // Add this state
  const [showShotCountConfirm, setShowShotCountConfirm] = useState(false);
  const cycleShotCountChanged =
    Number(editedMold?.cycleShotCount ?? 0) !== Number(mold.cycleShotCount ?? 0);

  const handleSave = () => {
    if (cycleShotCountChanged) {
      setShowShotCountConfirm(true);
      return;
    }
    onSave();
  };

  const [productParts, setProductParts] = useState<ProductPart[]>(mold.productParts || []);
  const [inserts, setInserts] = useState(mold.inserts || []);

  const handleRemovePart = async (partId: number) => {
    try {
      setIsLoading(true);
      await deleteAssociatedPart(mold.id, partId); // Update this line
      setShowDeleteConfirm(null);
    } catch (err) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPart = async (partId: number) => {
    try {
      setIsLoading(true);
      await associatePart(mold.id, partId);
      setShowSearchPartModal(false);
    } catch (err) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddInsert = async (insertId: number) => {
    try {
      setIsLoading(true);
      await associateInsert(mold.id, insertId); // Implement this function in your API hooks
      setShowSearchInsertModal(false);
    } catch (err) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const renderPartsTab = () => (
    <div>
      {!isEditing && error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
          {successMessage}
        </div>
      )}
      <ProductPartManager
        title={<h2 className="text-xl font-bold mb-4 flex items-center">
          <Wrench className="mr-2" size={20} />
          ชิ้นส่วนงานที่เกี่ยวข้อง
        </h2>}
        mode="selection"
        productParts={productParts}
        onPartsChange={(parts) => {
          setProductParts(parts);
          onFieldChange('productParts', parts);
        }}
        onAddPart={handleAddPart}
        onRemovePart={handleRemovePart}
        showDeleteButton={true}
        showEditButton={false}
        showActionButtons={true}
        moldId={mold.sbiId}
      />
    </div>
  );

  const renderInsertsTab = () => (
    <div>
      {!isEditing && error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
          {successMessage}
        </div>
      )}
      <InsertManager
        title={
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <PocketKnife className="mr-2" size={20} />
            อินเสิร์ทที่เกี่ยวข้อง
          </h2>
        }
        mode="selection"
        inserts={inserts}
        onInsertsChange={(newInserts) => {
          setInserts(newInserts);
          onFieldChange('inserts', newInserts);
        }}
        onAddInsert={handleAddInsert}
        onRemoveInsert={(insertId) => deleteAssociatedInsert(mold.id, insertId)}
        showDeleteButton={true}
        showEditButton={true}
        showActionButtons={true}
        showImageUploadButton={true}
        moldId={mold.sbiId}
      />
    </div>
  );

  const renderEventsTab = () => (
    <div className="min-h-4">
      <h2 className="text-xl font-bold mb-4 flex items-center">
        <Clock className="mr-2" size={20} />
        Audit logs
      </h2>
      <AuditLogsPanel entityType="mold" entityId={Number(mold.id)} />
    </div>
  );

  if (isLoading || !mold) {
    return (
      <MoldDetailSkeleton />
    );
  }

  const handleTabChange = (tab: MoldDetailTab) => {
    setActiveTab(tab);
    updatePreference({ moldDetailTab: tab });
  };

  const TabButton: React.FC<TabButtonProps> = ({
    value,
    label,
    icon: Icon,
  }) => (
    <button
      onClick={() => handleTabChange(value as MoldDetailTab)}
      className={`flex-1 py-3 px-4 text-base font-medium ${activeTab === value
        ? 'bg-white text-blue-600 border-b-2 border-blue-600'
        : 'text-gray-500 hover:text-gray-700 bg-gray-100'
        }`}
    >
      <div className="flex flex-row items-center justify-center">
        <Icon className="inline-block mr-2" size={20} />
        <p className="hidden sm:block">{label}</p>
      </div>
    </button>
  );

  return (
    <div className="px-1 py-2 sm:p-6 bg-gray-100 min-h-screen">
      <div className="mx-auto max-w-7xl space-y-1">
        <div className="bg-white rounded-lg shadow-md px-3 py-4 sm:p-6">
          <div className="flex gap-2 items-center mb-4 justify-between sm:justify-start">
            <h1 className="text-md sm:text-2xl font-bold">แม่พิมพ์ {mold.sbiId}</h1>
            <MoldStatus mold={mold} />
            <MoldImageTile moldId={mold.id} variant="cover" />
          </div>
          {isEditing && error && (
            <div className="mb-2 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          <div className="flex flex-col lg:flex-row gap-6">
            <MoldInformation
              mold={mold}
              editedMold={editedMold}
              isEditing={isEditing}
              onFieldChange={onFieldChange}
              fieldErrors={fieldErrors}
            />
            <div className="lg:w-64 flex flex-col gap-3">
              <ActionButtons
                isEditing={isEditing}
                isLoading={loading}
                onEdit={onEdit}
                onSave={handleSave}
                onCancel={onCancel}
                moldId={mold.id}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md">
          <div className="flex border-b">
            <TabButton value="tasks" label="ใบงาน" icon={FileText} />
            <TabButton value="parts" label="ชิ้นส่วนงาน" icon={Wrench} />
            <TabButton value="inserts" label="อินเสิร์ท" icon={PocketKnife} />
            <TabButton value="images" label="รูปภาพ" icon={ImageIcon} />
            <TabButton value="attachments" label="ไฟล์แนบ" icon={Paperclip} />
            {featureToggles.enableMoldGuides && (
              <TabButton value="guides" label="คู่มือ & เทคนิค" icon={BookOpen} />
            )}
            <TabButton value="events" label="Audit logs" icon={Clock} />
          </div>

          <div className="p-6">
            {activeTab === 'parts' ? renderPartsTab() : null}

            {activeTab === 'tasks' && (<MoldOrders moldId={mold.id} />)}

            {activeTab === 'events' && renderEventsTab()}

            {activeTab === 'inserts' && renderInsertsTab()}

            {activeTab === 'images' && <MoldImages moldId={mold.id} />}

            {activeTab === 'attachments' && <MoldAttachments moldId={mold.id} />}

            {featureToggles.enableMoldGuides && activeTab === 'guides' && (
              <MoldGuides moldId={mold.id} />
            )}
          </div>
        </div>
      </div>
      {showShotCountConfirm && (
        <ShotCountChangeConfirmDialog
          currentCycleShotCount={Number(mold.cycleShotCount ?? 0)}
          newCycleShotCount={Number(editedMold?.cycleShotCount ?? 0)}
          onCancel={() => setShowShotCountConfirm(false)}
          onConfirm={() => {
            setShowShotCountConfirm(false);
            onSave();
          }}
        />
      )}
      {showDeleteConfirm !== null && (
        <DeleteConfirmationDialog
          setShowDeleteConfirm={setShowDeleteConfirm}
          text={`คุณแน่ใจหรือไม่ว่าต้องการลบชิ้นส่วนนี้ออกจากแม่พิมพ์รหัส ${mold.sbiId}?`}
          onConfirm={() => handleRemovePart(showDeleteConfirm)}
        />
      )}
      {showSearchPartModal && (
        <SearchPartModal
          onClose={() => setShowSearchPartModal(false)}
          onAddPart={handleAddPart}
          associatedPartIds={mold.productParts.map((part) => part.id)} // Pass associated part IDs
        />
      )}
      {showSearchInsertModal && (
        <SearchInsertModal
          onClose={() => setShowSearchInsertModal(false)}
          onAddInsert={handleAddInsert}
          associatedInsertIds={mold.inserts.map((insert) => insert.id)} // Pass associated insert IDs
        />
      )}
    </div>
  );
};

export default MoldDetail;
